import { useMediaQuery } from '@mui/material';
import SortableTableHead from 'Components/TableBase/SortableTableHead';
import { StyledTable, StyledTableBody } from 'Components/TableBase/Table';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import React, { FC, useMemo } from 'react';
import theme from 'styles/theme';
import { TSortOrder } from 'types/pagination.types';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { parseOrderToOrderString } from 'utils/order.utils';
import { APP_QUERY_PARAMS } from 'utils/queryParam.utils';
import { COLUMNS_COMPONENT_ITEMS, EComponentItemTableColumnKeys } from '../utils/headerColumns';
import ComponentsTableRow from './ComponentItemTableRow';

interface IComponentsTableProps {
  components: IComponentItem[];
  hideHeader?: boolean;
  selected?: IComponentItem;
  columns: EComponentItemTableColumnKeys[];
  hidePartner?: boolean;
  hideDelete?: boolean;
  onSelect: (component: IComponentItem) => void;
  'data-testid'?: string;
}

const ComponentItemTable: FC<IComponentsTableProps> = ({
  components,
  columns,
  hideHeader,
  selected,
  hidePartner,
  hideDelete,
  onSelect,
  'data-testid': dataTestId = 'component-items-table',
}) => {
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xl'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const [query, setQuery] = useQueryParams({
    [APP_QUERY_PARAMS.order]: withDefault(StringParam, 'DESC'),
    [APP_QUERY_PARAMS.orderBy]: withDefault(
      StringParam,
      EComponentItemTableColumnKeys.MODIFIED_TIMESTAMP
    ),
  });
  const order = query[APP_QUERY_PARAMS.order] as TSortOrder | undefined;
  const orderBy = query[APP_QUERY_PARAMS.orderBy];

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'ASC';
    setQuery({
      [APP_QUERY_PARAMS.order]: isAsc ? 'DESC' : 'ASC',
    });
    setQuery({
      [APP_QUERY_PARAMS.orderBy]: property,
    });
  };

  const shownColumns = useMemo(
    () =>
      COLUMNS_COMPONENT_ITEMS.filter(
        ({ id }) =>
          columns.includes(id) &&
          (isSmallScreen || isExtraSmallScreen
            ? id !== EComponentItemTableColumnKeys.MODIFIED_TIMESTAMP
            : true) &&
          (isExtraSmallScreen ? id !== EComponentItemTableColumnKeys.ACTIONS : true)
      ),
    [columns, isSmallScreen, isExtraSmallScreen]
  );

  return (
    <StyledTable aria-labelledby="tableTitle" aria-label="enhanced table">
      {!hideHeader && (
        <SortableTableHead
          order={parseOrderToOrderString(order)}
          orderBy={orderBy}
          headerColumns={shownColumns}
          onSort={handleRequestSort}
        />
      )}
      <StyledTableBody data-testid={dataTestId}>
        {components.map(component => (
          <ComponentsTableRow
            selected={selected}
            key={component.id}
            componentItem={component}
            columns={shownColumns.map(({ id }) => id)}
            hidePartner={hidePartner}
            hideDelete={hideDelete}
            onRowClick={onSelect}
          />
        ))}
      </StyledTableBody>
    </StyledTable>
  );
};

export default ComponentItemTable;
